import { z } from 'zod';

import {
  createEntityAdapterSchema,
  EntityBaseSchema,
  EntityMetadataSchema,
  NonEmptyStringSchema,
  StringSchema,
} from '../../base/primitives';

// ============================================================================
// Property Schemas (Reusable across command, form, and projection schemas)
// ============================================================================

export const TopicNameSchema = NonEmptyStringSchema.describe(
  'Human-readable name for this topic. ' +
    'Must be unique within the project and cannot be empty. ' +
    "Examples: 'Order Status', 'Product Information', 'Returns Policy'",
);

export const TopicContentSchema = StringSchema.describe(
  'The content that defines what this topic covers. ' +
    'This is used by the RAG retrieval system to understand when to trigger this topic. ' +
    'Can include detailed information, instructions, or guidelines. You can also include variant values here ' +
    'by using {{attr:attribute_id}} where attribute_id is the ID of the attribute to replace. ' +
    "Examples: 'This is a test topic with {{attr:attr-1}} and {{attr:attr-2}}', this will then be replaced at runtime " +
    'with the actual values of the attributes from the current variant. Functions are not allowed to be referenced in the content ' +
    'field, any {{fn:function_id}} references will cause failures and must be in the actions field.',
);

export const TopicActionsSchema = StringSchema.describe(
  'Actions that the agent should take when this topic is triggered. ' +
    'Can include function calls, handoffs, or other behaviors. ' +
    "Examples: 'Send confirmation SMS', 'Transfer to sales team'. " +
    'You can also include variant values here by using {{attr:attribute_id}} and also reference functions by using {{fn:function_id}} where function_id is the ID of the function to reference. ' +
    "Examples: 'Send confirmation SMS to {{attr:phone_number}}', 'Transfer to sales team {{fn:sales_team_function_id}}', referencing functions will allow the LLM to use this at runtime.",
);

export const TopicIsActiveSchema = z
  .boolean()
  .describe(
    'Whether this topic is active and should be used by the agent. ' +
      'If false, the topic will not be used by the agent or shown on the UI.',
  );

export const TopicQuestionValueBaseSchema = z
  .string({ required_error: 'Example query is required' })
  .trim()
  .refine(val => !val.includes('|'), {
    message: 'Value can\'t contain the separator character "|"',
  })
  .describe(
    'Base schema for example query text. ' +
      "Trims whitespace and rejects pipe character '|'. " +
      'Does not enforce minimum length - use TopicQuestionValueSchema for that.',
  );

export const TopicQuestionValueSchema = z
  .string({ required_error: 'Example query is required' })
  .trim()
  .min(1, { message: 'Example query is required' })
  .refine(val => !val.includes('|'), {
    message: 'Value can\'t contain the separator character "|"',
  })
  .describe(
    'Example query text that should trigger this topic. ' +
      "Cannot contain the pipe character '|'. ",
  );

export const ExampleQuerySchema = z
  .object({
    query: TopicQuestionValueSchema,
  })
  .describe(
    'An example user query that should trigger this topic. ' +
      'Used to improve topic matching accuracy.',
  );

export const TopicReferencesSchema = z
  .object({
    sms: z
      .record(z.boolean(), { required_error: 'SMS references are required' })
      .describe(
        'Map of SMS template IDs referenced by this topic. ' +
          'Keys are SMS template IDs, values are always true. ' +
          'Tracks which SMS templates are used in this topic.',
      ),
    handoff: z
      .record(z.boolean(), {
        required_error: 'Handoff references are required',
      })
      .describe(
        'Map of handoff IDs referenced by this topic. ' +
          'Keys are handoff IDs, values are always true. ' +
          'Tracks which handoffs are used in this topic.',
      ),
    attributes: z
      .record(z.boolean(), {
        required_error: 'Attribute references are required',
      })
      .describe(
        'Map of attribute IDs referenced by this topic. ' +
          'Keys are attribute IDs, values are always true. ' +
          'Tracks which variant attributes are used in this topic.',
      ),
    globalFunctions: z
      .record(z.boolean(), {
        required_error: 'Global function references are required',
      })
      .describe(
        'Map of global function IDs called by this topic. ' +
          'Keys are function IDs, values are always true. ' +
          'Tracks which functions are invoked in this topic.',
      ),
    variables: z
      .record(z.boolean(), {
        required_error: 'Variable references are required',
      })
      .transform(state => state ?? {})
      .describe(
        'Map of variable IDs referenced by this topic. ' +
          'Keys are variable IDs, values are always true. ' +
          'Tracks which variables are used in this topic.',
      ),
    translations: z
      .record(z.boolean())
      .describe(
        'Map of translation IDs referenced by this topic. ' +
          'Keys are translation IDs, values are always true. ' +
          'Tracks which translations are used in this topic.',
      ),
  })
  .describe(
    'Tracks all references from this topic to other resources. ' +
      'Automatically managed by the system when references are created or removed.',
  );

export const EmbeddingModelSchema = z
  .string({ required_error: 'Embedding model is required' })
  .describe(
    'The embedding model used for topic matching. ' +
      'Determines how topics are vectorized and matched against user queries.',
  );

export const TopicSchema = EntityBaseSchema.merge(EntityMetadataSchema)
  .extend({
    name: TopicNameSchema,
    content: TopicContentSchema,
    actions: TopicActionsSchema,
    exampleQueries: z
      .array(ExampleQuerySchema, {
        required_error: 'Example queries array is required',
      })
      .max(100, {
        message: 'Maximum 100 example queries allowed',
      })
      .describe(
        'List of example queries that should trigger this topic. ' +
          'Maximum 100 queries allowed. ' +
          'Used to train topic matching.',
      ),
    references: TopicReferencesSchema,
    isActive: TopicIsActiveSchema,
  })
  .describe(
    'A knowledge base topic that represents a specific subject the agent can discuss. ' +
      'Topics are matched against user queries to determine relevant responses.',
  );

export const KnowledgeBaseSchema = z
  .object({
    topics: createEntityAdapterSchema(TopicSchema).describe(
      'Collection of active topics in the knowledge base. ' +
        'Each topic represents a specific subject the agent can handle.',
    ),
    embeddingModel: EmbeddingModelSchema,
    uninstantiatedTopics: createEntityAdapterSchema(TopicSchema).describe(
      'Collection of topics that have been created but not yet instantiated. ' +
        'Used during topic creation workflow.',
    ),
  })
  .describe(
    'Knowledge base configuration for the agent. ' +
      'Contains topics that define what the agent knows and can discuss.',
  );

export type Topic = z.infer<typeof TopicSchema>;
export type ExampleQuery = z.infer<typeof ExampleQuerySchema>;
export type TopicReferences = z.infer<typeof TopicReferencesSchema>;
export type KnowledgeBase = z.infer<typeof KnowledgeBaseSchema>;
